import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import './HeroMosaicPage.css';

const STORE_PRODUCT_URL = 'https://fabvoguestudio.myshopify.com/products/';
const MOBILE_SLOTS = 4;

function formatTs(value) {
  if (!value) return '—';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return String(value);
  return parsed.toLocaleString('en-IN', {
    timeZone: 'Asia/Kolkata',
    weekday: 'short',
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatDay(ymd) {
  const d = new Date(`${ymd}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return ymd;
  return d.toLocaleDateString('en-IN', { timeZone: 'UTC', weekday: 'short', day: '2-digit', month: 'short' });
}

function StatusPill({ status }) {
  return <span className={`hero-mosaic-pill hero-mosaic-pill-${status || 'unknown'}`}>{status || 'unknown'}</span>;
}

function DayRow({ day, today }) {
  const products = day.products || day.picks || [];
  return (
    <div className={`hero-mosaic-day ${day.date === today ? 'is-today' : ''}`}>
      <div className="hero-mosaic-day-head">
        <strong>{formatDay(day.date)}</strong>
        <code>{day.date}</code>
        {day.date === today && <span className="hero-mosaic-pill hero-mosaic-pill-today">Live today</span>}
        {day.status && <StatusPill status={day.status} />}
        {day.error && <span className="hero-mosaic-day-error">{day.error}</span>}
      </div>
      <div className="hero-mosaic-tiles">
        {products.map((p, i) => (
          <a
            key={p.id}
            className="hero-mosaic-tile"
            href={`${STORE_PRODUCT_URL}${p.handle}`}
            target="_blank"
            rel="noreferrer"
            title={p.title}
          >
            <div className="hero-mosaic-thumb">
              {p.imageUrl ? <img src={p.imageUrl} alt={p.title} loading="lazy" /> : <span>No image</span>}
              <span className="hero-mosaic-slot">{i + 1}</span>
              {i >= MOBILE_SLOTS && <span className="hero-mosaic-desktop-only">Desktop</span>}
            </div>
            <div className="hero-mosaic-tile-title">{p.title}</div>
            <div className="hero-mosaic-tile-meta">{p.fabric} · stock {p.stock ?? '—'}</div>
          </a>
        ))}
      </div>
    </div>
  );
}

function HeroMosaicPage() {
  const { currentUser } = useAuth();
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [startDate, setStartDate] = useState('');
  const [preview, setPreview] = useState(null);
  const [configDraft, setConfigDraft] = useState(null);

  const authedFetch = useCallback(async (url, options = {}) => {
    if (!currentUser) throw new Error('Not authenticated.');
    const token = await currentUser.getIdToken();
    const response = await fetch(url, {
      ...options,
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json', ...(options.headers || {}) },
    });
    const text = await response.text();
    let data = {};
    try {
      data = text ? JSON.parse(text) : {};
    } catch (_) {
      throw new Error(`Invalid server response from ${url}`);
    }
    if (!response.ok || data?.success === false) {
      throw new Error(data?.message || `Request failed for ${url}`);
    }
    return data;
  }, [currentUser]);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await authedFetch('/api/v1/hero-mosaic/overview');
      setOverview(data);
      setConfigDraft(data.config);
    } catch (err) {
      setError(err?.message || 'Failed to load hero mosaic.');
    } finally {
      setLoading(false);
    }
  }, [authedFetch]);

  useEffect(() => {
    load();
  }, [load]);

  const run = async (dryRun) => {
    const label = startDate ? `the 7 days from ${startDate}` : 'the coming Mon–Sun';
    if (!dryRun && !window.confirm(`Write ${label} to Shopify? This overwrites any existing entries for those dates and is live on the homepage.`)) return;
    setBusy(dryRun ? 'dry' : 'write');
    setError('');
    setNotice('');
    try {
      const data = await authedFetch('/api/v1/hero-mosaic/run', {
        method: 'POST',
        body: JSON.stringify({ dryRun, startDate: startDate || null }),
      });
      if (dryRun) {
        setPreview(data.run);
        setNotice(`Dry run: ${data.run.days.length} days planned from ${data.run.startDate}. Nothing was written.`);
      } else {
        setPreview(null);
        setNotice(`Run ${data.run.status}: ${data.run.days.filter((d) => d.status === 'written').length}/${data.run.days.length} days written.`);
        await load();
      }
    } catch (err) {
      setError(err?.message || 'Run failed.');
      await load();
    } finally {
      setBusy('');
    }
  };

  const saveConfig = async () => {
    setBusy('config');
    setError('');
    try {
      const data = await authedFetch('/api/v1/hero-mosaic/config', { method: 'PUT', body: JSON.stringify(configDraft) });
      setConfigDraft(data.config);
      setNotice('Settings saved.');
      await load();
    } catch (err) {
      setError(err?.message || 'Failed to save settings.');
    } finally {
      setBusy('');
    }
  };

  const lastRun = overview?.runs?.find((r) => !r.dryRun) || null;
  const rotation = overview?.rotation;
  const progressPct = useMemo(() => {
    if (!rotation?.eligible) return 0;
    return Math.min(100, Math.round((rotation.shownInCycle / rotation.eligible) * 100));
  }, [rotation]);
  const weeksLeft = rotation?.eligible
    ? Math.ceil(Math.max(0, rotation.eligible - rotation.shownInCycle) / ((overview?.config?.productsPerDay || 6) * 7))
    : null;
  const definition = overview?.definition;
  const configDirty = configDraft && overview?.config && JSON.stringify(configDraft) !== JSON.stringify(overview.config);

  return (
    <div className="hero-mosaic-page">
      <div className="hero-mosaic-header">
        <div>
          <h2>Hero Mosaic</h2>
          <p>Six products a day on the homepage hero mosaic, planned weekly (Sunday 18:00 IST) into the <code>hero_mosaic_day</code> metaobject. Every eligible product is shown once before any repeats.</p>
        </div>
        <button type="button" className="hero-mosaic-btn" onClick={load} disabled={loading || !!busy}>
          {loading ? 'Loading…' : 'Refresh'}
        </button>
      </div>

      {error && <div className="hero-mosaic-error">{error}</div>}
      {notice && <div className="hero-mosaic-notice">{notice}</div>}

      {definition && !definition.ok && (
        <div className="hero-mosaic-warning">
          <strong>Shopify metaobject definition needs attention — weekly runs will not write until fixed.</strong>
          <ul>{(definition.issues || []).map((issue) => <li key={issue}>{issue}</li>)}</ul>
        </div>
      )}

      {overview && (
        <div className="hero-mosaic-cards">
          <div className="hero-mosaic-card">
            <div className="hero-mosaic-kicker">Definition</div>
            <div className="hero-mosaic-value">{definition?.ok ? 'OK' : 'Not ready'}</div>
            <div className="hero-mosaic-caption">Storefront PUBLIC_READ, date + products</div>
          </div>
          <div className="hero-mosaic-card">
            <div className="hero-mosaic-kicker">Rotation · cycle {rotation?.cycle}</div>
            <div className="hero-mosaic-value">{rotation?.shownInCycle ?? 0}<span> / {rotation?.eligible ?? '—'}</span></div>
            <div className="hero-mosaic-progress"><div style={{ width: `${progressPct}%` }} /></div>
            <div className="hero-mosaic-caption">{weeksLeft != null ? `~${weeksLeft} weeks until every product has been shown` : 'Pool size known after first run'}</div>
          </div>
          <div className="hero-mosaic-card">
            <div className="hero-mosaic-kicker">Next weekly run</div>
            <div className="hero-mosaic-value hero-mosaic-value-sm">{formatTs(overview.nextRunAt)}</div>
            <div className="hero-mosaic-caption">{overview.config?.enabled ? 'Cron enabled' : 'Cron paused'}</div>
          </div>
          <div className="hero-mosaic-card">
            <div className="hero-mosaic-kicker">Last run</div>
            <div className="hero-mosaic-value hero-mosaic-value-sm">{lastRun ? <StatusPill status={lastRun.status} /> : '—'}</div>
            <div className="hero-mosaic-caption">{lastRun ? `${formatTs(lastRun.startedAt)} · ${lastRun.triggeredBy}` : 'No runs yet'}</div>
          </div>
        </div>
      )}

      <div className="hero-mosaic-panel">
        <div className="hero-mosaic-panel-header">
          <h2>Run</h2>
        </div>
        <div className="hero-mosaic-actions">
          <label>
            Start date (optional)
            <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
          </label>
          <button type="button" className="hero-mosaic-btn" onClick={() => run(true)} disabled={!!busy}>
            {busy === 'dry' ? 'Planning… (~1 min)' : 'Dry run (preview)'}
          </button>
          <button type="button" className="hero-mosaic-btn hero-mosaic-btn-primary" onClick={() => run(false)} disabled={!!busy || !definition?.ok}>
            {busy === 'write' ? 'Writing… (~1 min)' : 'Write week to Shopify'}
          </button>
          <span className="hero-mosaic-caption">Blank date = coming Monday. Re-running a week overwrites its 7 entries.</span>
        </div>
      </div>

      {preview && (
        <div className="hero-mosaic-panel hero-mosaic-panel-preview">
          <div className="hero-mosaic-panel-header">
            <h2>Dry run preview · {preview.startDate}</h2>
            <span className="hero-mosaic-caption">
              Pool {preview.poolStats?.eligible ?? '—'} eligible of {preview.poolStats?.active ?? '—'} active
              {preview.poolStats ? ` (no editorial image ${preview.poolStats.noEditorialImage}, unpublished ${preview.poolStats.unpublished}, sold out ${preview.poolStats.noAvailableVariant})` : ''}
            </span>
          </div>
          {preview.errors?.length > 0 && <div className="hero-mosaic-error">{preview.errors.join(' · ')}</div>}
          {preview.days.map((day) => <DayRow key={day.date} day={day} today={overview?.today} />)}
        </div>
      )}

      <div className="hero-mosaic-panel">
        <div className="hero-mosaic-panel-header">
          <h2>Scheduled on the homepage</h2>
          <span className="hero-mosaic-caption">Slots 1–4 show on mobile, all 6 on desktop.</span>
        </div>
        {overview?.days?.length
          ? overview.days.map((day) => <DayRow key={day.date} day={day} today={overview.today} />)
          : <div className="hero-mosaic-empty">Nothing written yet — the theme is showing its fixed fallback images.</div>}
      </div>

      <div className="hero-mosaic-columns">
        <div className="hero-mosaic-panel">
          <div className="hero-mosaic-panel-header"><h2>Settings</h2></div>
          {configDraft && (
            <div className="hero-mosaic-config">
              <label className="hero-mosaic-check">
                <input type="checkbox" checked={!!configDraft.enabled} onChange={(e) => setConfigDraft({ ...configDraft, enabled: e.target.checked })} />
                Weekly cron enabled
              </label>
              <label className="hero-mosaic-check">
                <input type="checkbox" checked={!!configDraft.avoidSameFabric} onChange={(e) => setConfigDraft({ ...configDraft, avoidSameFabric: e.target.checked })} />
                Avoid two products of the same fabric in one day
              </label>
              <label>
                Slot order
                <select value={configDraft.slotOrder} onChange={(e) => setConfigDraft({ ...configDraft, slotOrder: e.target.value })}>
                  <option value="stock">Highest stock first (less likely to sell out mid-week)</option>
                  <option value="random">Random</option>
                </select>
              </label>
              <label>
                Delete Shopify entries older than (days)
                <input type="number" min="1" value={configDraft.retentionDays} onChange={(e) => setConfigDraft({ ...configDraft, retentionDays: e.target.value })} />
              </label>
              <label>
                Alert email
                <input type="email" value={configDraft.alertEmail || ''} onChange={(e) => setConfigDraft({ ...configDraft, alertEmail: e.target.value })} />
              </label>
              <button type="button" className="hero-mosaic-btn hero-mosaic-btn-primary" onClick={saveConfig} disabled={!configDirty || !!busy}>
                {busy === 'config' ? 'Saving…' : 'Save settings'}
              </button>
            </div>
          )}
        </div>

        <div className="hero-mosaic-panel">
          <div className="hero-mosaic-panel-header"><h2>Recent runs</h2></div>
          {overview?.runs?.length ? (
            <div className="hero-mosaic-runs">
              {overview.runs.map((r) => (
                <details key={r.runId} className="hero-mosaic-run">
                  <summary>
                    <StatusPill status={r.status} />
                    <span>{formatTs(r.startedAt)}</span>
                    <span className="hero-mosaic-caption">{r.dryRun ? 'dry run · ' : ''}{r.triggeredBy} · week {r.startDate}</span>
                  </summary>
                  <div className="hero-mosaic-run-body">
                    <div>Timezone: {r.timezone?.timezone || '—'} {r.timezone && !r.timezone.ok && '⚠️'}</div>
                    <div>Pool: {r.poolStats ? `${r.poolStats.eligible} eligible / ${r.poolStats.active} active` : '—'}</div>
                    <div>Days: {(r.dayStatuses || []).map((d) => `${d.date.slice(5)} ${d.status}`).join(' · ') || '—'}</div>
                    <div>Cleanup: {r.cleanup ? (r.dryRun ? `would delete ${(r.cleanup.wouldDelete || []).length}` : `deleted ${(r.cleanup.deleted || []).length}`) : '—'}</div>
                    {r.errors?.length > 0 && <div className="hero-mosaic-day-error">{r.errors.join(' · ')}</div>}
                  </div>
                </details>
              ))}
            </div>
          ) : <div className="hero-mosaic-empty">No runs yet.</div>}
        </div>
      </div>
    </div>
  );
}

export default HeroMosaicPage;
